// Plain-language help for the GitHub Copilot policy controls.
// Labels, purposes, selected outcomes, and interactions. Input examples and
// conditional workflow guidance live in controlGuidance.jsx.
//
// Source: the managed-settings schema shipped in Copilot CLI 1.0.93, vendored
// as shared-agent-config/tests/fixtures/copilot_cli_1.0.93_managed_settings.schema.json,
// and the server catalog descriptions built from it.

import { valueAt } from "../catalogDocument.js";

export const optionLabel = (value) => {
    const words = String(value).replaceAll("_", " ").replaceAll("-", " ");
    return words[0].toUpperCase() + words.slice(1);
};

const P = Object.freeze({
    loginOrgs: "/forceLoginOrgs",
    remoteControl: "/remoteControl",
    model: "/model",
    effort: "/effortLevel",
    contextTier: "/contextTier",
    autoTier: "/autoTier",
    bypass: "/permissions/disableBypassPermissionsMode",
    deny: "/permissions/deny",
    ask: "/permissions/ask",
    allow: "/permissions/allow",
    limitTo: "/permissions/limitTo",
    mcpAllow: "/allowedMcpServers",
    mcpDeny: "/deniedMcpServers",
    mcpManagedOnly: "/allowManagedMcpServersOnly",
    pluginOnly: "/strictPluginOnlyCustomization",
    hooksOnly: "/allowManagedHooksOnly",
    marketplaces: "/strictKnownMarketplaces",
});
export const POINTERS = P;

/// Label and optional purpose. Input formats and conditional impacts live in controlGuidance.
export const COPILOT_COPY = Object.freeze({
    [P.loginOrgs]: ["Sign-in organizations", "Require membership in an approved GitHub organization before Copilot can run."],
    [P.remoteControl]: ["Remote control", "Control remote access to Copilot CLI sessions hosted on this machine."],
    [P.model]: ["Starting model", "Set a starting model; this does not restrict which models users may choose."],
    [P.effort]: ["Starting reasoning effort", "Set the starting reasoning effort when a specific model is selected."],
    [P.contextTier]: ["Starting context tier", "Choose the starting context size when a specific model is selected."],
    [P.autoTier]: ["Auto routing tier", "Set an overridable starting tier or require a fixed tier for automatic model routing."],
    [P.bypass]: ["Allow-all mode", "Prevent blanket approval of tools, paths, and URLs through allow-all modes or flags."],
    [P.deny]: ["Blocked operations", "Block matching operations even when a user would approve them."],
    [P.ask]: ["Operations that need approval", "Require approval for selected operations while leaving other operations to their existing rules."],
    [P.allow]: ["Operations allowed without asking", "Remove routine approval interruptions for selected operations."],
    [P.limitTo]: ["Network domain boundary", "Restrict domain requests checked by Copilot to approved destinations."],
    [P.mcpAllow]: ["Allowed MCP servers", "Limit which tool integrations may load. Adding an entry excludes unlisted servers, except built-in GitHub servers."],
    [P.mcpDeny]: ["Blocked MCP servers", "Block specific tool integrations. Built-in GitHub servers are exempt."],
    [P.mcpManagedOnly]: ["Only managed MCP servers", "Control whether personal and project allowlists participate in MCP selection."],
    [P.pluginOnly]: ["Customization from plugins only", "Which customizations may come only from plugins or managed settings."],
    [P.hooksOnly]: ["Block user, repository, and plugin hooks", "Restrict automatic hook execution to managed hooks and required plugins."],
    [P.marketplaces]: ["Allowed marketplace sources", "Limit where users may add plugin marketplaces. Adding an entry excludes unlisted sources."],
});

export const SURFACE_COPY = Object.freeze({
    mcp: ["MCP servers from plugins only", "Whether personal and repository MCP servers are ignored."],
    content: ["Skills, agents and hooks from plugins only", "Which personal and repository customizations are ignored."],
});

export const SURFACE_LABELS = Object.freeze({ skills: "Skills", agents: "Agents", hooks: "Hooks", mcp: "MCP servers" });

/// What managed sessions do for some kinds of the plugin-only lock.
export function surfaceOnEndpoints(members, document) {
    const value = valueAt(document, P.pluginOnly);
    const locked = members.filter((member) => value === true || (Array.isArray(value) && value.includes(member)));
    if (!locked.length) return { written: false, text: "Nothing is locked. People and repositories may add these." };
    return { written: true, text: `${joinAnd(locked.map((member) => SURFACE_LABELS[member] ?? optionLabel(member)))} come only from plugins or managed settings.` };
}

/// One plain sentence on each closed choice.
export const CHOICE_DETAILS = Object.freeze({
    [P.bypass]: {
        disable: "Allow-all mode and the allow-all flags cannot be used.",
    },
    [P.contextTier]: {
        default: "The usual context size.",
        long_context: "A larger context on models that support it.",
    },
    [P.remoteControl]: {
        enabled: "Sessions can be controlled from other devices.",
        disabled: "Sessions cannot be controlled from other devices.",
        requireSSO: "Only accounts in a listed organization or domain can control sessions.",
    },
});

const NOT_CONFIGURED = "No value is imposed by this policy. Other configuration still applies.";

/// Descriptions for the three cards of a yes/no setting.
export const BOOL_DETAILS = Object.freeze({
    [P.mcpManagedOnly]: { unset: NOT_CONFIGURED, on: "Only this policy and managed plugins decide which MCP servers load.", off: "Personal and project allowlists also count." },
    [P.hooksOnly]: { unset: NOT_CONFIGURED, on: "Only managed hooks and hooks from required plugins run.", off: "Hooks load as usual." },
});

const isList = (value) => Array.isArray(value);
const isMap = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const count = (n, noun, plural = `${noun}s`) => `${n} ${n === 1 ? noun : plural}`;
const joinAnd = (list) => (list.length <= 1 ? list.join("") : `${list.slice(0, -1).join(", ")} and ${list.at(-1)}`);

const listLine = (value, { none, some }) => {
    if (!isList(value)) return { written: false, text: none };
    return { written: true, text: some(value.length) };
};

const boolLine = (value, { on, off, unset }) => {
    if (value === true) return { written: true, text: on };
    if (value === false) return { written: true, text: off };
    return { written: false, text: unset };
};

const concreteModel = (model) => typeof model === "string" && model !== "" && model !== "auto";

/// What a managed session does under the current selection.
export function onEndpoints(pointer, document) {
    const value = valueAt(document, pointer);
    const details = BOOL_DETAILS[pointer];
    switch (pointer) {
        case P.loginOrgs:
            if (!isList(value)) return { written: false, text: "This policy adds no organization restriction. Account access and other policies still apply." };
            if (!value.length) return { written: true, text: "This policy requires no GitHub organization membership. Other access requirements still apply." };
            return { written: true, text: `Copilot runs only for accounts in ${joinAnd(value)}. Accounts without that membership and API keys are refused.` };
        case P.remoteControl: {
            const mode = isMap(value) ? value.mode : undefined;
            if (!mode) return { written: false, text: "Nothing is written. Remote control follows the person's own setting." };
            if (mode === "disabled") return { written: true, text: "Copilot CLI sessions cannot be controlled from other devices." };
            if (mode === "enabled") return { written: true, text: "Copilot CLI sessions can be controlled from other devices." };
            const n = (value.githubDotComOrganizations?.length ?? 0) + (value.githubEnterpriseCloudDomains?.length ?? 0);
            return { written: true, text: `Only accounts authorized in ${count(n, "listed organization or domain", "listed organizations or domains")} can control Copilot CLI sessions.` };
        }
        case P.model:
            if (!value) return { written: false, text: "No starting model is imposed by this policy. Other configuration and Copilot defaults apply." };
            return { written: true, text: `New conversations start with ${value}. People can change the model.` };
        case P.effort:
            if (!value) return { written: false, text: "Nothing is written. New conversations start with the model's default effort." };
            return { written: true, text: `New conversations start with ${value} effort when Starting model names a specific model.` };
        case P.contextTier:
            if (!value) return { written: false, text: "Nothing is written. New conversations start with the default context tier." };
            return { written: true, text: `New conversations start with the ${optionLabel(value)} tier when Starting model names a specific model.` };
        case P.autoTier:
            if (typeof value === "string") return { written: true, text: `Auto uses the ${value} tier. People and repositories cannot change it.` };
            if (isMap(value)) return { written: true, text: `Auto starts with the ${value.overridable} tier. People and repositories can change it.` };
            return { written: false, text: "Nothing is written. Auto uses the person's own tier." };
        case P.bypass:
            if (value === "disable") return { written: true, text: "Allow-all mode and the allow-all flags cannot be used. Approval prompts still work." };
            return { written: false, text: "Nothing is written. People can turn on allow-all mode." };
        case P.deny:
            return listLine(value, { none: "This policy adds no rules for these operations. Other permission rules still apply.", some: (n) => `${count(n, "rule")} ${n === 1 ? "blocks" : "block"} matching operations. Ask and allow rules cannot override them.` });
        case P.ask:
            return listLine(value, { none: "This policy adds no rules for these operations. Other permission rules still apply.", some: (n) => `${count(n, "rule")} ${n === 1 ? "requires" : "require"} approval for matching operations.` });
        case P.allow:
            return listLine(value, { none: "This policy adds no rules for these operations. Other permission rules still apply.", some: (n) => `${count(n, "rule")} ${n === 1 ? "lets" : "let"} matching operations run without a prompt, unless a block or ask rule matches.` });
        case P.limitTo:
            if (!isList(value)) return { written: false, text: "This policy adds no domain boundary. Other network restrictions still apply." };
            if (!value.length) return { written: true, text: "All domain requests covered by Copilot's domain check are denied." };
            return { written: true, text: `Copilot's domain check permits only ${count(value.length, "listed domain rule")}. Requests to other domains are denied without a prompt.` };
        case P.mcpAllow:
            if (!isList(value)) return { written: false, text: "This policy adds no MCP allowlist. Other configuration still applies." };
            if (!value.length) return { written: true, text: "No MCP server may load except built-in GitHub servers." };
            return { written: true, text: `Only servers that match ${count(value.length, "entry", "entries")} may load, plus built-in GitHub servers.` };
        case P.mcpDeny:
            if (!isList(value) || !value.length) return { written: isList(value), text: "No MCP server is blocked by this policy." };
            return { written: true, text: `Servers that match ${count(value.length, "entry", "entries")} never load.` };
        case P.marketplaces:
            if (!isList(value)) return { written: false, text: "This policy adds no marketplace restriction. Other configuration still applies." };
            if (!value.length) return { written: true, text: "No marketplace may be added." };
            return { written: true, text: `People may add marketplaces only from ${count(value.length, "listed source")}.` };
        default:
            if (details) return boolLine(value, { on: details.on, off: details.off, unset: "No value is imposed by this policy. Other configuration still applies." });
            return null;
    }
}

/// Lines that appear only when two settings collide.
export function interactions(pointer, document) {
    const model = valueAt(document, P.model);
    const out = [];
    const add = (scope, target, text) => out.push({ scope, target, text });
    switch (pointer) {
        case P.effort:
        case P.contextTier:
            if (valueAt(document, pointer) !== undefined && !concreteModel(model)) add("Starting model", P.model, "Copilot uses this value only when Starting model names a specific model. It is not set to one, so this value has no effect.");
            break;
        case P.autoTier:
            if (valueAt(document, pointer) !== undefined && concreteModel(model)) add("Starting model", P.model, `New conversations start with ${model}, so this tier applies only after a person picks auto.`);
            break;
        default:
            break;
    }
    return out;
}

/// English for one MCP server matcher.
export function describeMcpMatcher(matcher) {
    if (typeof matcher?.serverUrl === "string") return `A remote server whose URL matches ${matcher.serverUrl}. A * matches any text.`;
    if (isList(matcher?.serverCommand)) return `A local server started with exactly ${matcher.serverCommand.join(" ")}.`;
    if (typeof matcher?.serverName === "string") return `A server named ${matcher.serverName}.`;
    return "An entry this page cannot describe.";
}

const SOURCE_LABELS = Object.freeze({
    github: "GitHub repository",
    git: "Git URL",
    url: "URL",
    npm: "npm package",
    file: "File",
    directory: "Folder",
    hostPattern: "Host pattern",
    pathPattern: "Path pattern",
});
export const sourceKindLabel = (kind) => SOURCE_LABELS[kind] ?? kind;

/// English for one marketplace source descriptor.
export function describeMarketplaceSource(source) {
    const label = sourceKindLabel(source?.source);
    const locator = source?.repo ?? source?.url ?? source?.package ?? source?.path ?? source?.hostPattern ?? source?.pathPattern;
    if (!locator) return "A source this page cannot describe.";
    const extras = [source.ref ? `ref ${source.ref}` : "", source.source !== "file" && source.source !== "directory" && source.path ? `path ${source.path}` : ""].filter(Boolean);
    return `${label} ${locator}${extras.length ? `, ${extras.join(", ")}` : ""}.`;
}
